import React, { useEffect, useState, useMemo } from 'react';
import { getBulletin, getBulletinList, getDocument } from '../../Utils/frontStageApi';

interface BulletinItem {
  bid: number;
  date: string;
  tag: string;
  title: string;
  text?: string;
  loading?: boolean;
}

interface DocItem {
  did?: number;
  date: string;
  title: string;
  size: string;
  url: string;
}

const TAG_FILTERS = ['全部', '系統維護', '最新消息', '新功能上線'];

export const NewsPage: React.FC = () => {
  const [bulletins, setBulletins] = useState<BulletinItem[]>([]);
  const [documents, setDocuments] = useState<DocItem[]>([]);
  const [newsFilter, setNewsFilter] = useState<string>('全部');
  const [bulletinLoading, setBulletinLoading] = useState<boolean>(true);
  const [bulletinError, setBulletinError] = useState<string | null>(null);
  const [docLoading, setDocLoading] = useState<boolean>(true);
  const [docError, setDocError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch live bulletins
    setBulletinLoading(true);
    setBulletinError(null);
    getBulletinList(0, 1)
      .then((data) => {
        if (data?.list && Array.isArray(data.list)) {
          setBulletins(
            data.list.map((item) => ({
              bid: item.bid,
              date: item.date,
              tag: item.tag,
              title: item.title,
            }))
          );
        } else {
          setBulletins([]);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch bulletins:', err);
        setBulletinError('無法連線至公告伺服器，請確認後端連線或 CORS 設定。');
        setBulletins([]);
      })
      .finally(() => {
        setBulletinLoading(false);
      });

    // Fetch live documents
    setDocLoading(true);
    setDocError(null);
    getDocument(0)
      .then((data: any) => {
        if (data?.list && Array.isArray(data.list)) {
          setDocuments(
            data.list.map((d: any) => ({
              did: d.did,
              date: d.date,
              title: d.title,
              size: d.file_size || '—',
              url: d.link,
            }))
          );
        } else {
          setDocuments([]);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch documents:', err);
        setDocError('無法連線至文件伺服器，請確認後端連線或 CORS 設定。');
        setDocuments([]);
      })
      .finally(() => {
        setDocLoading(false);
      });
  }, []);

  const handleToggleDetails = async (bid: number, e: React.SyntheticEvent<HTMLDetailsElement>) => {
    const detailsEl = e.currentTarget;
    if (!detailsEl.open) return;

    const target = bulletins.find((b) => b.bid === bid);
    if (target && !target.text && !target.loading) {
      setBulletins((prev) =>
        prev.map((b) => (b.bid === bid ? { ...b, loading: true } : b))
      );
      try {
        const detail = await getBulletin(bid);
        const text = detail?.content?.text || '無內文';
        setBulletins((prev) =>
          prev.map((b) => (b.bid === bid ? { ...b, text, loading: false } : b))
        );
      } catch {
        setBulletins((prev) =>
          prev.map((b) => (b.bid === bid ? { ...b, text: '無法讀取公告內容', loading: false } : b))
        );
      }
    }
  };

  const filteredBulletins = useMemo(() => {
    if (newsFilter === '全部') return bulletins;
    return bulletins.filter((b) => b.tag === newsFilter);
  }, [bulletins, newsFilter]);

  return (
    <>
      <h1>最新消息</h1>
      <p className="dc-lead">服務公告、系統維護與相關文件下載。</p>

      {/* 公告群組 */}
      <section style={{ marginTop: '28px' }}>
        <h2 style={{ margin: '0 0 12px' }}>公告</h2>
        <div className="dc-filter" aria-label="公告分類">
          {TAG_FILTERS.map((tag) => (
            <button
              key={tag}
              type="button"
              className="dc-button"
              aria-pressed={newsFilter === tag}
              onClick={() => setNewsFilter(tag)}
            >
              {tag}
            </button>
          ))}
        </div>

        {bulletinLoading ? (
          <div className="dc-empty">載入公告資料中...</div>
        ) : bulletinError ? (
          <div className="dc-empty" style={{ color: 'var(--dc-accent)' }}>
            {bulletinError}
          </div>
        ) : filteredBulletins.length === 0 ? (
          <div className="dc-empty">
            {newsFilter === '全部'
              ? '目前沒有公告資料。'
              : `目前沒有「${newsFilter}」公告。`}
          </div>
        ) : (
          filteredBulletins.map((b, i) => (
            <details
              key={b.bid ?? i}
              open={i === 0 && newsFilter === '全部'}
              style={{ borderBottom: '1px solid var(--dc-line)', padding: '14px 0' }}
              onToggle={(e) => handleToggleDetails(b.bid, e)}
            >
              <summary
                style={{
                  cursor: 'pointer',
                  fontWeight: 550,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'var(--dc-muted)',
                    fontSize: '13.5px',
                    minWidth: '85px',
                  }}
                >
                  {b.date}
                </span>
                <span
                  className="dc-label"
                  style={
                    b.tag === '最新消息'
                      ? { color: 'var(--dc-gold)', borderColor: 'var(--dc-gold)' }
                      : { color: 'var(--dc-accent)', borderColor: 'var(--dc-accent)' }
                  }
                >
                  {b.tag}
                </span>
                <strong style={{ color: 'var(--dc-ink)' }}>{b.title}</strong>
              </summary>
              <p
                style={{
                  margin: '12px 0 6px',
                  lineHeight: 1.75,
                  color: 'var(--dc-muted)',
                  whiteSpace: 'pre-line',
                  paddingLeft: '4px',
                }}
              >
                {b.loading ? '載入中...' : b.text || '載入內文中...'}
              </p>
            </details>
          ))
        )}
      </section>

      {/* 文件下載群組 */}
      <section style={{ marginTop: '40px' }}>
        <h2 style={{ margin: '0 0 8px' }}>文件下載</h2>
        <p style={{ margin: '0 0 16px', color: 'var(--dc-muted)', fontSize: '14px' }}>
          提供數位憑證與系統應用相關說明文件、操作指引與宣傳圖卡下載。
        </p>
        {docLoading ? (
          <div className="dc-empty">載入文件資料中...</div>
        ) : docError ? (
          <div className="dc-empty" style={{ color: 'var(--dc-accent)' }}>
            {docError}
          </div>
        ) : documents.length === 0 ? (
          <div className="dc-empty">目前沒有可下載的文件資料。</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="dc-table">
              <thead>
                <tr>
                  <th style={{ width: '140px' }}>上傳日期</th>
                  <th>主旨</th>
                  <th style={{ width: '120px' }}>檔案尺寸</th>
                  <th style={{ width: '110px', textAlign: 'center' }}>下載</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((d, idx) => (
                  <tr key={d.did ?? idx}>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{d.date}</td>
                    <td>
                      <strong>{d.title}</strong>
                    </td>
                    <td style={{ color: 'var(--dc-muted)' }}>{d.size}</td>
                    <td style={{ textAlign: 'center' }}>
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={`${d.title}.pdf`}
                        className="dc-button"
                        style={{ padding: '4px 14px', minHeight: '32px', fontSize: '13px' }}
                      >
                        下載 ↗
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};

export default NewsPage;
